import React from 'react';
import PageHero from './PageHero';
import {
  Users, Home, Gamepad2, Brush, Monitor, Clock, MessageSquare, Lightbulb,
  MessageCircle, Hash, Palette, UserCheck, Bike, Leaf,
  CheckCircle, Target, Badge, Star, Sparkles, Heart,
  Compass, BookOpen, Award
} from 'lucide-react';
import preschoolImg from '../assets/PRESCHOOL.png';
import ibLogo from '../assets/IB CONTINUUM.png';

// ── Reusable components ────────────────────────────────────────────────────────

function SectionTitle({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-16">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
          <Sparkles className="w-3.5 h-3.5 text-gold" />
          <span className="text-gold-ink text-[11px] font-bold tracking-[0.2em] uppercase">{eyebrow}</span>
        </div>
      )}
      <h2 className="text-3xl md:text-5xl font-heading font-bold text-navy leading-tight mb-4 uppercase">{title}</h2>
      {subtitle && (
        <p className="text-ink/75 text-[16px] md:text-[17px] leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function FeatureBox({ icon, title, desc, accent }) {
  const accents = {
    orange: 'border-[#f97316] bg-[#fff7f0]',
    blue:   'border-[#3b82f6] bg-[#eff6ff]',
    green:  'border-[#22c55e] bg-[#f0fdf4]',
    red:    'border-[#ef4444] bg-[#fef2f2]',
    pink:   'border-[#ec4899] bg-[#fdf2f8]',
    purple: 'border-[#a855f7] bg-[#faf5ff]',
    yellow: 'border-[#eab308] bg-[#fefce8]',
    violet: 'border-[#8b5cf6] bg-[#f5f3ff]',
  };
  const iconColors = {
    orange: 'text-[#f97316]', blue: 'text-[#3b82f6]', green: 'text-[#22c55e]',
    red: 'text-[#ef4444]', pink: 'text-[#ec4899]', purple: 'text-[#a855f7]',
    yellow: 'text-[#eab308]', violet: 'text-[#8b5cf6]',
  };
  return (
    <div className={`rounded-2xl border-l-4 p-6 h-full shadow-sm hover:shadow-md transition-shadow duration-300 ${accents[accent]}`}>
      <div className={`mb-4 ${iconColors[accent]}`}>{icon}</div>
      <h4 className="text-lg font-heading font-semibold text-navy mb-2">{title}</h4>
      <p className="text-ink/70 text-[14.5px] leading-relaxed">{desc}</p>
    </div>
  );
}

function CurriculumFeature({ icon, title, desc, align }) {
  const isLeft = align === 'left';
  return (
    <div className={`flex items-start gap-4 mb-10 ${isLeft ? 'flex-row-reverse text-right' : ''}`}>
      <div className={`shrink-0 w-12 h-12 rounded-xl bg-navy flex items-center justify-center text-white shadow-md`}>
        {icon}
      </div>
      <div className={isLeft ? 'text-right' : ''}>
        <h3 className="text-lg font-heading font-semibold text-navy mb-1">{title}</h3>
        <p className="text-ink/70 text-[14.5px] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function InfoCard({ icon, iconColor, title, items, checkColor }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-line p-8 h-full hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-12 h-12 rounded-full ${iconColor} flex items-center justify-center shrink-0`}>
          {icon}
        </div>
        <h3 className="text-xl font-heading font-semibold text-navy">{title}</h3>
      </div>
      <ul className="space-y-4">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle size={18} className={`shrink-0 mt-0.5 ${checkColor}`} />
            <span className="text-ink/80 text-[14.5px] leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function PreschoolPage({ setPage, t }) {
  const pData = t?.preschoolPage || {};
  const intro = pData?.intro || {};
  const stages = pData?.stages || [];
  const cta = pData?.cta || {};

  const handleNavigate = (pageKey) => {
    if (setPage) setPage(pageKey);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stageIcons = [
    <Heart key="heart" className="w-6 h-6 text-white" />,
    <Sparkles key="sparkles" className="w-6 h-6 text-white" />,
    <Compass key="compass" className="w-6 h-6 text-white" />,
    <Award key="award" className="w-6 h-6 text-white" />
  ];

  const navCategory = t?.nav?.[2]?.label || 'Academics';
  const homeLabel = t?.nav?.[0]?.label || 'Home';

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink pb-0">

      {/* ── Page Hero ─────────────────────────────────────────────────────────── */}
      <PageHero
        eyebrow={pData.eyebrow || navCategory}
        title={pData.title || 'Preschool (Ages 2–5)'}
        subtitle={
          pData.subtitle ||
          'The early years are a special time when children begin to discover who they are and how they connect with the world around them.'
        }
        breadcrumbs={pData.breadcrumbs || [homeLabel, navCategory, 'Preschool']}
        bgImage={preschoolImg}
      />

      {/* ── Intro Section: A Caring & Inspiring Environment ─────────────────── */}
      <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px] py-16 lg:py-24">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              <span className="text-gold-ink text-[11px] font-bold tracking-[0.2em] uppercase">
                {intro.eyebrow || 'EARLY YEARS AT GMIS'}
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-heading font-bold text-navy mb-6 leading-tight uppercase">
              {intro.title || 'A Caring & Inspiring Environment'}
            </h2>

            <div className="space-y-5 text-ink/80 text-[16.5px] md:text-[18px] leading-[1.75]">
              <p className="m-0">
                {intro.p1 ||
                  'The early years are a special time when children begin to discover who they are and how they connect with the world around them. Our Preschool programme combines a tailor-made GMIS curriculum with the IB Primary Years Programme (PYP) Early Years approach to create a caring and inspiring environment where children can grow with confidence.'}
              </p>
              <p className="m-0">
                {intro.p2 ||
                  'From Toddler to Preparatory, learning evolves with each child, supporting their individual development while fostering a natural curiosity and a lifelong love for learning.'}
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 pt-6 border-t border-line/70">
              <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-xl border border-line shadow-sm">
                <img src={ibLogo} alt="IB World School" className="h-7 w-auto object-contain" />
                <span className="text-xs font-heading font-bold text-navy uppercase tracking-wider">
                  IB PYP Early Years Candidate School
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-gold-ink">
                <CheckCircle className="w-4 h-4 text-gold" />
                <span>Tailor-Made GMIS Foundation</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/10 rounded-2xl -z-0" />
            <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-navy/10 rounded-full -z-0" />
            <div className="relative z-10 bg-white p-3 rounded-3xl shadow-xl border border-line/80">
              <img
                src={preschoolImg}
                alt="GMIS Preschool Students"
                className="rounded-2xl w-full h-[400px] md:h-[460px] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Core Four Stages: Toddler, Nursery, Kindergarten, Preparatory ──────── */}
      <div className="bg-white py-16 lg:py-24 border-y border-line/60">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={t?.utilAdm === 'Penerimaan' ? 'JENJANG PRASEKOLAH' : 'EARLY YEARS PATHWAYS'}
            title={t?.utilAdm === 'Penerimaan' ? 'Empat Tahap Pembelajaran Usia Dini' : 'Four Stages of Early Learning'}
            subtitle={
              t?.utilAdm === 'Penerimaan'
                ? 'Dari usia 2 tahun hingga persiapan masuk SD, setiap jenjang disesuaikan dengan ritme perkembangan dan rasa ingin tahu unik anak.'
                : 'From age 2 to primary school readiness, each programme is carefully crafted to nurture emotional security, conceptual thinking, and joyous discovery.'
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {stages.map((stage, idx) => (
              <div
                key={stage.id || idx}
                className="bg-[#fcfdfd] rounded-2xl p-8 lg:p-10 border border-slate-200/80 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-xl hover:border-gold/50 transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  {/* Top bar with badge */}
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3.5">
                      <div className="w-12 h-12 rounded-xl bg-navy text-white flex items-center justify-center shadow-md group-hover:bg-gold transition-colors">
                        {stageIcons[idx] || <BookOpen className="w-6 h-6 text-white" />}
                      </div>
                      <div>
                        <h3 className="m-0 font-heading font-bold text-2xl text-navy">
                          {stage.name}
                        </h3>
                        <div className="text-xs font-bold text-gold-ink tracking-wider uppercase">
                          {stage.age}
                        </div>
                      </div>
                    </div>

                    <span className="px-3.5 py-1 rounded-full text-[11px] font-heading font-semibold uppercase tracking-wider bg-gold/15 text-gold-ink border border-gold/30">
                      {stage.badge}
                    </span>
                  </div>

                  {/* Subtitle / Framework Tag */}
                  <div className="inline-block mb-4 text-[13px] font-bold text-navy/85 bg-navy/5 px-3 py-1 rounded-lg">
                    {stage.tagline}
                  </div>

                  {/* Description Paragraphs */}
                  <div className="space-y-4 text-ink/75 text-[14.5px] md:text-[15px] leading-relaxed mb-6 font-sans">
                    <p className="m-0">{stage.p1}</p>
                    <p className="m-0">{stage.p2}</p>
                  </div>
                </div>

                {/* Key Features Pills */}
                {stage.features && stage.features.length > 0 && (
                  <div className="pt-6 border-t border-line/70">
                    <div className="text-xs font-bold uppercase tracking-wider text-navy mb-3">
                      {t?.utilAdm === 'Penerimaan' ? 'Sorotan Kurikulum' : 'Key Focus Elements'}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {stage.features.map((feat, fIdx) => (
                        <div key={fIdx} className="flex items-center gap-2 text-[13px] text-ink/80">
                          <CheckCircle className="w-4 h-4 text-gold shrink-0" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* ── Facilities & Special Programs ────────────────────────────────────── */}
      <div className="py-20 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={pData.facilitiesEyebrow || 'Campus & Resources'}
            title={pData.facilitiesTitle || 'Facilities & Special Programs'}
            subtitle={
              t?.utilAdm === 'Penerimaan'
                ? 'Dirancang khusus untuk kenyamanan, keselamatan, dan kebebasan bereksplorasi bagi anak-anak usia dini.'
                : 'Purposefully designed spaces promoting safety, comfort, motor skills, and joyful social interaction.'
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureBox accent="orange" icon={<Users size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Rasio Kelas Kecil' : 'Small Class Sizes'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Rasio siswa-guru rendah untuk memastikan perhatian personal dan pengasuhan individual.' : 'Low student-to-teacher ratio ensuring personalised attention and individual care.'} />
            <FeatureBox accent="blue" icon={<Home size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Ruang Ramah Anak' : 'Child-Friendly Classrooms'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Ruangan ber-AC yang cerah, aman, nyaman, dan mendukung eksplorasi bebas.' : 'Bright, air-conditioned rooms designed for safety, comfort, and free exploration.'} />
            <FeatureBox accent="green" icon={<Gamepad2 size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Metode Belajar Bermain' : 'Play & Learn Model'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Mengintegrasikan musik, dongeng, permainan sensorik, dan gerak ke dalam pembelajaran harian.' : 'Integrating music, storytelling, games, and movement into everyday learning.'} />
            <FeatureBox accent="red" icon={<Brush size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Sudut Aktivitas Sensorik' : 'Activity Corners'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Area khusus untuk seni rupa, teka-teki, balok susun, dan eksplorasi praktis.' : 'Dedicated spaces for art, puzzles, sensory play, and hands-on discovery.'} />
            <FeatureBox accent="pink" icon={<Monitor size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Media Pembelajaran Interaktif' : 'Interactive Learning Tools'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Alat bantu visual cerdas dan media edukatif untuk merangsang rasa ingin tahu anak.' : 'Smart boards, audio-visual aids, and digital displays to engage young minds.'} />
            <FeatureBox accent="purple" icon={<Clock size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Rutinitas Harian Lembut' : 'Gentle Daily Routine'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Jadwal seimbang memadukan belajar terstruktur dengan istirahat, makan sehat, dan bermain bebas.' : 'Balanced schedule blending structured learning with rest, meals, and outdoor play.'} />
            <FeatureBox accent="yellow" icon={<MessageSquare size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Ekspresi & Rasa Percaya Diri' : 'Expression & Confidence'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Mendorong anak-anak berbicara bebas, bertanya tanpa ragu, dan membangun kepercayaan diri.' : 'Safe spaces encouraging children to express freely, ask questions, and build confidence.'} />
            <FeatureBox accent="violet" icon={<Lightbulb size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Fondasi Keterampilan Dini' : 'Early Skill Development'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Meningkatkan motorik halus dan kasar, kemampuan berkomunikasi, dan pra-literasi.' : 'Enhancing fine and gross motor skills, communication, and pre-literacy foundations.'} />
          </div>
        </div>
      </div>

      {/* ── Curriculum Focus Areas ───────────────────────────────────────────── */}
      <div className="bg-white py-20 lg:py-24 border-y border-line/60">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={pData.focusEyebrow || 'Learning Framework'}
            title={pData.focusTitle || 'Preschool Curriculum Focus Areas'}
            subtitle={
              t?.utilAdm === 'Penerimaan'
                ? 'Pengembangan holistik yang mencakup aspek intelektual, sosial, emosional, dan fisik anak.'
                : 'A balanced interdisciplinary approach nurturing cognitive, social-emotional, and creative capability.'
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            {/* Left features */}
            <div>
              <CurriculumFeature align="left"
                icon={<MessageCircle size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Bahasa & Komunikasi' : 'Language & Communication'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Membangun kosakata awal, fonik, membaca pemula, dan percakapan aktif melalui cerita.' : 'Building early vocabulary, phonics, pre-reading skills and basic conversation through stories.'} />
              <CurriculumFeature align="left"
                icon={<Hash size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Numerasi & Konsep Dini' : 'Early Numeracy'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Mengenalkan angka, membilang, bentuk, warna, dan pola melalui permainan nyata.' : 'Introducing numbers, counting, shapes, colours, and patterns through hands-on play.'} />
              <CurriculumFeature align="left"
                icon={<Palette size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Seni Kreatif & Musik' : 'Creative Arts'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Menggambar, mewarnai, lagu anak, dan gerak tari untuk imajinasi serta ekspresi diri.' : 'Drawing, colouring, music, rhymes, and dance for imagination and self-expression.'} />
            </div>

            {/* Centre image */}
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-3xl scale-110" />
                <img
                  src={preschoolImg}
                  alt="Preschool at GMIS"
                  className="relative z-10 rounded-2xl shadow-xl w-full max-w-[340px] h-[420px] object-cover object-top mx-auto border-4 border-white"
                />
              </div>
            </div>

            {/* Right features */}
            <div>
              <CurriculumFeature align="right"
                icon={<UserCheck size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Keterampilan Sosial & Empati' : 'Social Skills'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Berbagi, kerja sama tim, empati, dan rasa hormat yang dipupuk lewat kegiatan kelompok.' : 'Sharing, teamwork, empathy, and respect developed through group activities.'} />
              <CurriculumFeature align="right"
                icon={<Bike size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Perkembangan Fisik & Gerak' : 'Physical Development'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Bermain di luar ruangan, yoga anak, dan aktivitas gerak untuk menyempurnakan motorik.' : 'Outdoor play, yoga, and movement activities to enhance motor and gross skills.'} />
              <CurriculumFeature align="right"
                icon={<Leaf size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Eksplorasi Alam Sekitar' : 'Nature Exploration'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Kunjungan taman sekolah, mengenali tanaman, dan pembelajaran kepedulian lingkungan.' : 'Story walks, school garden visits, and simple environmental awareness lessons.'} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Detailed Info Cards ──────────────────────────────────────────────── */}
      {/* <div className="py-20 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={t?.utilAdm === 'Penerimaan' ? 'INFORMASI PRAKTIS' : 'PRACTICAL INFORMATION'}
            title={pData.programmeDetailsTitle || 'Preschool Programme Overview'}
            subtitle={
              t?.utilAdm === 'Penerimaan'
                ? 'Semua informasi penting mengenai jadwal harian, tata busana seragam, dan panduan untuk orang tua.'
                : 'Essential guidance covering teaching approach, school timings, uniforms, and family partnership.'
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            // Approach
            <InfoCard
              icon={<Lightbulb size={22} className="text-blue-600" />}
              iconColor="bg-blue-50"
              checkColor="text-blue-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Pendekatan & Metode Mengajar' : 'Our Approach & Teaching Methodology'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Pendekatan inkuiri berbasis bermain selaras dengan standar IB PYP Early Years.',
                      'Fokus mengembangkan kemampuan kognitif, motorik, sosial, dan emosional secara menyeluruh.',
                      'Lingkungan hangat, aman, dan inklusif yang dirancang untuk memupuk rasa ingin tahu anak.',
                      'Konsep dasar bahasa, matematika, dan keterampilan hidup diajarkan lewat dongeng, seni, dan permainan.',
                      'Guru terlatih dan berbelas kasih dengan spesialisasi pendidikan anak usia dini (ECE).'
                    ]
                  : [
                      'IB-aligned inquiry-based and play-centred learning approach.',
                      'Focus on developing cognitive, motor, social, and emotional skills holistically.',
                      'Safe, warm, and inclusive environment designed to nurture every child’s curiosity.',
                      'Foundational concepts in English, Mathematics, and Life Skills taught through stories, art, and play.',
                      'Trained and compassionate teachers specialising in early childhood education (ECE).'
                    ]
              }
            />

            // Program Focus
            <InfoCard
              icon={<Target size={22} className="text-green-600" />}
              iconColor="bg-green-50"
              checkColor="text-green-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Fokus Pengembangan Program' : 'Programme Focus'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Fondasi kuat untuk literasi dan numerasi melalui metode yang gembira dan interaktif.',
                      'Membangun kepercayaan diri, kemandirian, dan kebiasaan positif sejak usia dini.',
                      'Pengembangan emosional dan sosial melalui kegiatan kelompok dan kolaborasi.',
                      'Pengenalan alam, apresiasi seni dan musik untuk perkembangan serba seimbang.',
                      'Kolaborasi orang tua dan guru dalam mendampingi perjalanan unik setiap anak.'
                    ]
                  : [
                      'Strong foundation for literacy and numeracy through joyful, playful methods.',
                      'Building self-confidence, independence, and positive habits from an early age.',
                      'Emotional and social development through group activities and collaborative storytelling.',
                      'Introduction to nature, music, and creative arts for all-round development.',
                      'Parent-teacher collaboration to support each child’s unique learning journey.'
                    ]
              }
            />

            // Timings
            <InfoCard
              icon={<Clock size={22} className="text-cyan-600" />}
              iconColor="bg-cyan-50"
              checkColor="text-cyan-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Jadwal & Jam Belajar' : 'Timings & Schedule'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Senin hingga Jumat: 07.30 – 13.30 WIB.',
                      'Program Toddler diselenggarakan 3 hari per minggu untuk penyesuaian yang optimal.',
                      'Periode adaptasi bertahap untuk anak yang baru pertama kali masuk sekolah.',
                      'Pilihan kepulangan lebih awal selama minggu-minggu pertama masa transisi.'
                    ]
                  : [
                      'Monday to Friday: 7:30 AM – 1:30 PM.',
                      'Toddler programme structured 3 days per week for gentle pacing and settling in.',
                      'Flexible settling-in period for new children joining for the first time.',
                      'Half-day options available during initial transition weeks.'
                    ]
              }
            />

            // Uniform
            <InfoCard
              icon={<Badge size={22} className="text-amber-600" />}
              iconColor="bg-amber-50"
              checkColor="text-amber-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Seragam Sekolah' : 'Uniform & Dress Code'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Seragam GMIS yang nyaman dan berwarna cerah dirancang untuk kebebasan bergerak dan bermain.',
                      'Sepatu dengan perekat velcro atau slip-on yang mudah digunakan sendiri oleh anak.',
                      'Pakaian olahraga (PE kit) digunakan pada hari-hari aktivitas fisik.',
                      'Satu set pakaian ganti cadangan wajib disimpan di sekolah untuk situasi darurat.'
                    ]
                  : [
                      'Comfortable, colourful GMIS uniforms designed for free movement and active play.',
                      'Shoes with Velcro or slip-on design for easy independence by little ones.',
                      'PE kit (tracksuit and soft shoes) required on scheduled physical activity days.',
                      'An extra set of clothes to be kept at school at all times for emergencies.'
                    ]
              }
            />
          </div>

          // Note to Parents – full width
          <div className="bg-[#fef8f0] border border-gold/30 rounded-2xl p-8 shadow-sm mt-8">
            <h3 className="text-lg font-heading font-bold text-navy mb-5 flex items-center gap-3">
              <Star size={18} className="text-gold" />
              <span>{t?.utilAdm === 'Penerimaan' ? 'Catatan Penting untuk Orang Tua' : 'A Note to Parents'}</span>
            </h3>
            <div className="flex flex-col gap-3.5">
              {(t?.utilAdm === 'Penerimaan'
                ? [
                    'Kami mendorong orang tua untuk memprioritaskan kesiapan emosional dan kesejahteraan anak dibandingkan tekanan akademis. Setiap pencapaian kecil patut dirayakan.',
                    'Komunikasi terbuka dan berkala dengan wali kelas sangat dianjurkan untuk memahami perkembangan serta kebutuhan unik anak Anda.',
                    'Masa adaptasi adalah hal yang sangat wajar. Setiap anak menyesuaikan diri dengan kecepatannya masing-masing, dan guru kami senantiasa mendampingi dengan penuh kesabaran.'
                  ]
                : [
                    'We encourage parents to prioritise their child’s emotional readiness and wellbeing over academic pressure. Every milestone is celebrated.',
                    'Open and regular communication with our class teachers is warmly encouraged to understand your child’s unique progress and needs.',
                    'A settling-in period is completely normal. Every child adapts at their own pace, and our teachers provide full, patient support throughout.'
                  ]
              ).map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                  </div>
                  <p className="text-ink/80 text-[14.5px] leading-relaxed m-0">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div> */}

      {/* ── Section 5: CTA (solid bg-navy with NO gradient) ────────────────── */}
      <div className="w-full bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-16">
          <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
            <div className="relative z-10">
              <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight uppercase">
                {cta.title || "Ready to Begin Your Child's Journey?"}
              </h3>
              <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10">
                {cta.subtitle ||
                  'Schedule a campus visit today and see our world-class early childhood facilities and caring environment for yourself.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => handleNavigate('schedule-visit')}
                  className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300 cursor-pointer shadow-md"
                >
                  {cta.button || 'Schedule a Visit'}
                </button>
                <button
                  onClick={() => handleNavigate('admissions-process')}
                  className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20 cursor-pointer"
                >
                  {cta.secondaryButton || 'Explore Admissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
